import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';
import { 
  Brain, Plus, Loader2, CheckCircle, Building2, Upload, XCircle, 
  AlertCircle, FileText, Trash2, Sparkles, ClipboardCheck, UserPlus, 
  Files, Paperclip, Check, FolderArchive, GraduationCap,
  Send, Zap, Activity, Bot, User, Clock, BookOpen, Eye, Coins,
  Star, ArrowRight, ThumbsUp, Search, Globe
} from 'lucide-react';
import CreditCostBadge, { CREDIT_FEATURES, CreditCostInline } from './CreditCostBadge';
import { triggerCreditUpdate } from './CreditIndicator';

// Shimmer animation styles
const shimmerStyles = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
.animate-shimmer {
  animation: shimmer 2s infinite linear;
  background-size: 200% 100%;
}
`;

// Inject shimmer styles
if (typeof document !== 'undefined') {
  const styleId = 'ai-profiler-shimmer-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = shimmerStyles;
    document.head.appendChild(style);
  }
}

// AI Avatar
const AIAvatar = ({ size = 'md', isThinking = false }) => {
  const sizeClasses = size === 'lg' ? 'w-14 h-14' : size === 'sm' ? 'w-8 h-8' : 'w-10 h-10';
  const iconSize = size === 'lg' ? 'w-7 h-7' : size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <div className={`${sizeClasses} rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center flex-shrink-0 shadow-md ${isThinking ? 'animate-pulse' : ''}`}>
      <Brain className={`${iconSize} text-primary-foreground`} />
    </div>
  );
};

// User Avatar
const UserAvatar = () => (
  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
    <User className="w-5 h-5 text-muted-foreground" />
  </div>
);

// Thinking Indicator
const ThinkingIndicator = () => (
  <div className="flex items-start gap-3 animate-fade-in">
    <AIAvatar isThinking={true} />
    <div className="flex-1 p-4 rounded-xl bg-muted/50 border-l-2 border-primary">
      <div className="flex items-center gap-3">
        <div className="flex gap-1">
          <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
        <span className="text-sm text-muted-foreground">Thinking...</span>
      </div>
    </div>
  </div>
);

// Enhanced Progress Loader Component
const ProgressLoader = ({ progress, stage, message, subMessage }) => {
  const stages = [
    { key: 'upload', label: 'Uploading', icon: Upload },
    { key: 'extract', label: 'Extracting', icon: FileText },
    { key: 'analyze', label: 'Analyzing', icon: Brain },
    { key: 'recommend', label: 'Recommending', icon: Sparkles },
  ];
  
  const currentStageIndex = stages.findIndex(s => s.key === stage);
  
  return (
    <div className="flex items-start gap-3 animate-fade-in">
      <AIAvatar isThinking={true} />
      <div className="flex-1 p-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10 border border-primary/20">
        {/* Stage indicators */}
        <div className="flex items-center justify-between mb-4">
          {stages.map((s, idx) => {
            const Icon = s.icon;
            const isActive = idx === currentStageIndex;
            const isCompleted = idx < currentStageIndex;
            
            return (
              <div key={s.key} className="flex flex-col items-center gap-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                  isActive ? 'bg-primary text-primary-foreground scale-110 shadow-lg shadow-primary/30' : 
                  isCompleted ? 'bg-green-500 text-white' : 
                  'bg-muted text-muted-foreground'
                }`}>
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Icon className={`w-5 h-5 ${isActive ? 'animate-pulse' : ''}`} />
                  )}
                </div>
                <span className={`text-xs font-medium ${isActive ? 'text-primary' : isCompleted ? 'text-green-600' : 'text-muted-foreground'}`}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Progress bar */}
        <div className="relative h-3 bg-muted rounded-full overflow-hidden mb-3">
          <div 
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse" />
          </div>
          {/* Shimmer effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer" 
               style={{ backgroundSize: '200% 100%', animation: 'shimmer 2s infinite' }} />
        </div>
        
        {/* Progress text */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-sm font-medium text-foreground">{message}</span>
          </div>
          <span className="text-sm font-bold text-primary">{progress}%</span>
        </div>
        
        {/* Sub message */}
        {subMessage && (
          <p className="text-xs text-muted-foreground mt-2 animate-pulse">{subMessage}</p>
        )}
      </div>
    </div>
  );
};

// Select Options helpers
const UniversityOptions = ({ universities }) => {
  return universities.map(uni => (
    <SelectItem key={uni.id} value={uni.id}>{uni.name}</SelectItem>
  ));
};

const DocTypeOptions = ({ docTypes }) => {
  return docTypes.map(dt => (
    <SelectItem key={dt.id} value={dt.id}>{dt.name}</SelectItem>
  ));
};

const AIProfilerChat = () => {
  const { api } = useAuth();
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  
  // Data
  const [universities, setUniversities] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  // UPDATED FLOW: init -> university -> documents -> select_docs -> analysis -> suggestions -> create -> complete
  const [currentPhase, setCurrentPhase] = useState('init');
  
  // Selections
  const [selectedUniversity, setSelectedUniversity] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]); // NEW: Documents selected for analysis
  const [selectedDocType, setSelectedDocType] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  
  // Progress loader state
  const [progressStage, setProgressStage] = useState('upload'); // upload, extract, analyze, recommend
  const [progressMessage, setProgressMessage] = useState('');
  const [progressSubMessage, setProgressSubMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // NEW: Analysis results
  const [analysisResult, setAnalysisResult] = useState(null);
  const [suggestedPrograms, setSuggestedPrograms] = useState([]);
  const [recommendedDegreeType, setRecommendedDegreeType] = useState('');
  
  // Degree type selection - required for BPO agents
  const [degreeTypes, setDegreeTypes] = useState([]);
  const [selectedDegreeType, setSelectedDegreeType] = useState('');
  
  // Nationality selection
  const [countries, setCountries] = useState([]);
  const [selectedNationality, setSelectedNationality] = useState('');
  const [nationalitySearchQuery, setNationalitySearchQuery] = useState('');
  
  // Manual program selection state
  const [showManualProgramDropdown, setShowManualProgramDropdown] = useState(false);
  const [allUniversityPrograms, setAllUniversityPrograms] = useState([]);
  const [loadingAllPrograms, setLoadingAllPrograms] = useState(false);
  const [programSearchQuery, setProgramSearchQuery] = useState('');
  
  const [createdAppId, setCreatedAppId] = useState(null);
  const [createdAppType, setCreatedAppType] = useState(null); // 'bpo_application' or null for regular
  const [isCreating, setIsCreating] = useState(false);
  
  const fileInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const initializedRef = useRef(false);
  
  // Credit check state
  const [hasCredits, setHasCredits] = useState(true);
  const [creditBalance, setCreditBalance] = useState(0);
  
  // Auto-scroll to bottom when messages, documents, or thinking state changes
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking, documents.length]);
  
  // Check credits and fetch data
  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    
    const fetchDataAndStart = async () => {
      try {
        const creditRes = await api().get('/credits/balance');
        const balance = creditRes.data?.credits?.balance || 0;
        setCreditBalance(balance);
        
        if (balance < 1) {
          setHasCredits(false);
          return;
        }
        
        // Get user info to determine if BPO agent
        const userRes = await api().get('/auth/me');
        const userType = userRes.data?.user_type;
        const isBpoAgent = userType === 'bpo_agent';
        
        // Fetch universities based on user type
        let universitiesEndpoint = '/master-data?type=university';
        if (isBpoAgent) {
          universitiesEndpoint = '/bpo/universities';
        }
        
        const [uniRes, docTypesRes, degreeTypesRes] = await Promise.all([
          api().get(universitiesEndpoint),
          api().get('/master-data?type=document_type'),
          api().get('/master-data?type=degree_type')
        ]);
        
        // Handle BPO universities response format
        const universitiesData = isBpoAgent 
          ? (uniRes.data || [])
          : (uniRes.data || []);
        setUniversities(universitiesData);
        setDocumentTypes(docTypesRes.data || []);
        setDegreeTypes(degreeTypesRes.data || []);
        
        const sessionRes = await api().post('/ai-profiler/sessions', { title: 'AI Counselor Session' });
        setSessionId(sessionRes.data.id);
        setCurrentPhase('university');
        setIsInitializing(false);
        addAIMessage("Hello! I'm your AI Counselor. I'll help you create a student application.\n\n**Step 1:** Let's start by selecting the target university.", 'university_select');
      } catch (error) {
        console.error('Failed to initialize:', error);
        setIsInitializing(false);
        toast.error('Failed to start AI Counselor');
      }
    };
    fetchDataAndStart();
  }, [api]);
  
  // Add AI message helper
  const addAIMessage = (content, component = null) => {
    setMessages(prev => [...prev, {
      type: 'ai',
      content,
      component,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
  };
  
  // Add user message helper
  const addUserMessage = (content) => {
    setMessages(prev => [...prev, {
      type: 'user',
      content,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }]);
  };
  
  // Fetch all programs for the selected university
  const fetchAllUniversityPrograms = async () => {
    if (!selectedUniversity?.id) return;
    setLoadingAllPrograms(true);
    try {
      const response = await api().get(`/academic-hierarchy/programs?university_id=${selectedUniversity.id}`);
      setAllUniversityPrograms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch university programs:', error);
      setAllUniversityPrograms([]);
      toast.error('Failed to load programs');
    } finally {
      setLoadingAllPrograms(false);
    }
  };
  
  // Handle manual program selection click
  const handleManualProgramClick = async () => {
    setShowManualProgramDropdown(true);
    setProgramSearchQuery(''); // Reset search when opening
    if (allUniversityPrograms.length === 0) {
      await fetchAllUniversityPrograms();
    }
  };
  
  // Handle selecting a program from the manual dropdown
  const handleManualProgramSelect = (programId) => {
    const program = allUniversityPrograms.find(p => p.id === programId);
    if (program) {
      handleProgramSelection({
        id: program.id,
        name: program.name,
        reason: 'Manually selected from university programs'
      });
      setShowManualProgramDropdown(false);
      setProgramSearchQuery(''); // Reset search after selection
    }
  };
  
  // Start new application (reset state)
  const startNewApplication = async () => {
    setIsLoading(true);
    try {
      const response = await api().post('/ai-profiler/sessions', {
        title: `Application - ${new Date().toLocaleDateString()}`
      });
      setSessionId(response.data.id);
      setMessages([]);
      setCurrentPhase('university');
      setSelectedUniversity(null);
      setSelectedProgram(null);
      setDocuments([]);
      setAnalysisResult(null);
      setSuggestedPrograms([]);
      setRecommendedDegreeType('');
      setCreatedAppId(null);
      setShowManualProgramDropdown(false);
      setAllUniversityPrograms([]);
      setProgramSearchQuery('');
      setSelectedNationality('');
      setNationalitySearchQuery('');
      setCountries([]);
      
      setTimeout(() => {
        addAIMessage(
          "Hello! I'm your AI Counselor. I'll guide you through creating a student application.\n\n**Step 1:** Let's start by selecting the target university.",
          'university_select'
        );
      }, 500);
      
      toast.success('New application started');
    } catch (error) {
      toast.error('Failed to start new application');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle university selection - NEW FLOW: Go directly to documents
  const handleUniversitySelect = async (uniId) => {
    const uni = universities.find(u => u.id === uniId);
    setSelectedUniversity(uni);
    
    addUserMessage(`I want to apply to ${uni.name}`);
    setIsThinking(true);
    
    // Ensure session exists before making the API call
    if (!sessionId) {
      console.error('No session ID available');
      setIsThinking(false);
      addAIMessage("I encountered an issue - please refresh the page and try again.");
      return;
    }
    
    try {
      console.log('Selecting university with session:', sessionId, 'university:', uniId);
      await api().post(`/ai-profiler/sessions/${sessionId}/select-university`, { university_id: uniId });
      
      // Fetch countries for nationality selection
      try {
        const countriesRes = await api().get('/master-data?type=country');
        setCountries(countriesRes.data || []);
      } catch (e) {
        console.error('Failed to fetch countries:', e);
      }
      
      setTimeout(() => {
        setIsThinking(false);
        setCurrentPhase('nationality');
        addAIMessage(
          `Great choice! **${uni.name}** is selected.\n\n**Step 2:** Please select the student's **nationality**. This helps determine the required documents and eligibility criteria.`,
          'nationality_select'
        );
      }, 1000);
    } catch (error) {
      console.error('Error selecting university:', error.response?.status, error.response?.data || error.message);
      setIsThinking(false);
      if (error.response?.status === 404) {
        addAIMessage("Session expired. Please refresh the page to start a new session.");
      } else {
        addAIMessage("I encountered an issue saving your selection. Please try again.");
      }
    }
  };
  
  // Handle nationality selection
  const handleNationalitySelect = (nationalityId) => {
    const country = countries.find(c => c.id === nationalityId);
    if (!country) return;
    
    setSelectedNationality(nationalityId);
    setIsThinking(true);
    addUserMessage(`Nationality: ${country.name}`);
    
    setTimeout(() => {
      setIsThinking(false);
      setCurrentPhase('documents');
      addAIMessage(
        `Perfect! Nationality set to **${country.name}**.\n\n**Step 3:** Now please upload the student's documents (academic transcripts, degrees, test scores, CV, passport, etc.).\n\nI'll analyze them to:\n- Extract the student's profile\n- Verify the nationality\n- Suggest the most suitable degree type\n- Recommend the best programs based on their education\n\nYou can upload individual files or a ZIP archive containing all documents.`,
        'document_upload'
      );
    }, 800);
  };
  
  // Handle file upload
  const handleFileUpload = async (file, docTypeId = null) => {
    if (!file) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    setProgressStage('upload');
    setProgressMessage(`Uploading ${file.name}...`);
    setProgressSubMessage('Preparing document for processing');
    
    const formData = new FormData();
    formData.append('file', file);
    if (docTypeId) formData.append('document_type_id', docTypeId);
    
    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 15, 90));
      }, 200);
      
      const endpoint = docTypeId 
        ? `/ai-profiler/sessions/${sessionId}/upload`
        : `/ai-profiler/sessions/${sessionId}/upload-auto`;
      
      const response = await api().post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setProgressMessage('Upload complete!');
      
      const upload = response.data.upload || response.data;
      const docType = docTypeId ? documentTypes.find(dt => dt.id === docTypeId) : null;
      
      const newDoc = {
        ...upload,
        document_type_name: docType?.name || upload.document_type_name || upload.detected_type || 'Auto-detected',
        document_type_id: docTypeId || upload.document_type_id
      };
      
      setDocuments(prev => [...prev, newDoc]);
      // Auto-select new document for analysis
      setSelectedDocuments(prev => [...prev, newDoc.id]);
      setSelectedDocType('');
      
      addUserMessage(`Uploaded: ${file.name}`);
      
      // Get the renamed filename from response (or fallback to original)
      const renamedName = newDoc.filename || file.name;
      const wasRenamed = renamedName !== file.name;
      
      setIsThinking(true);
      setTimeout(() => {
        setIsThinking(false);
        setUploadProgress(0);
        const renameNote = wasRenamed ? `\n\n📝 Renamed to: **${renamedName}** (original: ${file.name})` : '';
        addAIMessage(`Document uploaded successfully as **${newDoc.document_type_name}**.${renameNote}\n\n✅ Auto-selected for analysis. You can:\n- Upload more documents\n- Uncheck documents you don't want to analyze\n- Click **"Analyze Selected Documents"** when ready`, 'document_upload');
      }, 500);
      
    } catch (error) {
      setUploadProgress(0);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // Handle ZIP upload
  const handleZipUpload = async (file) => {
    if (!file || !file.name.endsWith('.zip')) {
      toast.error('Please select a ZIP file');
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    setProgressStage('upload');
    setProgressMessage(`Uploading ${file.name}...`);
    setProgressSubMessage('Processing ZIP archive');
    addUserMessage(`Uploading ZIP: ${file.name}`);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Progress simulation for ZIP
      let progress = 0;
      const stages = [
        { at: 20, stage: 'upload', msg: 'Uploading archive...', sub: 'Transferring files to server' },
        { at: 50, stage: 'extract', msg: 'Extracting documents...', sub: 'Unpacking ZIP contents' },
        { at: 80, stage: 'extract', msg: 'Classifying documents...', sub: 'Auto-detecting document types' },
      ];
      
      const progressInterval = setInterval(() => {
        progress += 5;
        if (progress <= 90) {
          setUploadProgress(progress);
          const currentStage = stages.find(s => s.at > progress - 5 && s.at <= progress);
          if (currentStage) {
            setProgressStage(currentStage.stage);
            setProgressMessage(currentStage.msg);
            setProgressSubMessage(currentStage.sub);
          }
        }
      }, 300);
      
      const response = await api().post(`/ai-profiler/sessions/${sessionId}/upload-zip`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setProgressMessage('Extraction complete!');
      
      const processed = response.data.processed_files || [];
      const newDocs = processed.map(pf => ({
        id: pf.id,
        filename: pf.filename,  // Use renamed filename from backend
        original_filename: pf.original_filename || pf.filename,  // Keep original for reference
        file_type: pf.file_type,
        document_type_name: pf.detected_type || 'Auto-detected',
        document_type_id: pf.document_type_id
      }));
      
      setDocuments(prev => [...prev, ...newDocs]);
      // Auto-select all extracted documents for analysis
      setSelectedDocuments(prev => [...prev, ...newDocs.map(d => d.id)]);
      
      // Count how many were renamed
      const renamedCount = newDocs.filter(d => d.filename !== d.original_filename).length;
      const renameNote = renamedCount > 0 ? `\n\n📝 ${renamedCount} document${renamedCount > 1 ? 's' : ''} auto-renamed based on detected type.` : '';
      
      setIsThinking(true);
      setTimeout(() => {
        setIsThinking(false);
        setUploadProgress(0);
        addAIMessage(`Extracted **${processed.length} documents** from the ZIP file.${renameNote}\n\n✅ All documents auto-selected for analysis. You can:\n- Uncheck documents you don't want to analyze\n- Upload more documents\n- Click **"Analyze Selected Documents"** when ready`, 'document_upload');
      }, 800);
      
    } catch (error) {
      setUploadProgress(0);
      toast.error(error.response?.data?.detail || 'ZIP extraction failed');
    } finally {
      setIsUploading(false);
      if (zipInputRef.current) zipInputRef.current.value = '';
    }
  };
  
  // Delete document
  const handleDeleteDocument = async (docId) => {
    try {
      await api().delete(`/ai-profiler/sessions/${sessionId}/uploads/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      // Also remove from selected documents
      setSelectedDocuments(prev => prev.filter(id => id !== docId));
      toast.success('Document removed');
    } catch (error) {
      toast.error('Failed to remove document');
    }
  };
  
  // NEW: Run analysis and get program suggestions
  const runAnalysisAndSuggest = async () => {
    if (documents.length === 0) {
      toast.error('Please upload at least one document');
      return;
    }
    
    addUserMessage("Please analyze the uploaded documents and suggest suitable programs");
    setIsAnalyzing(true);
    setAnalysisProgress(0);
    setCurrentPhase('analysis');
    setIsThinking(false); // We'll use ProgressLoader instead
    
    // Set initial progress state
    setProgressStage('analyze');
    setProgressMessage('Reading documents...');
    setProgressSubMessage('Preparing files for AI analysis');
    
    try {
      // Enhanced progress simulation with stages
      let progress = 0;
      const stages = [
        { at: 10, stage: 'analyze', msg: 'Reading documents...', sub: 'Loading uploaded files' },
        { at: 25, stage: 'analyze', msg: 'Extracting text...', sub: 'Processing document contents' },
        { at: 40, stage: 'analyze', msg: 'Analyzing profile...', sub: 'Identifying student information' },
        { at: 55, stage: 'analyze', msg: 'Evaluating qualifications...', sub: 'Assessing education & experience' },
        { at: 70, stage: 'recommend', msg: 'Finding programs...', sub: 'Matching with suitable courses' },
        { at: 85, stage: 'recommend', msg: 'Generating recommendations...', sub: 'Preparing personalized suggestions' },
      ];
      
      const progressInterval = setInterval(() => {
        progress += 3;
        if (progress <= 90) {
          setAnalysisProgress(progress);
          const currentStage = stages.find(s => s.at > progress - 3 && s.at <= progress);
          if (currentStage) {
            setProgressStage(currentStage.stage);
            setProgressMessage(currentStage.msg);
            setProgressSubMessage(currentStage.sub);
          }
        }
      }, 600);
      
      // Use longer timeout for AI analysis (3 minutes)
      const response = await api().post(`/ai-profiler/sessions/${sessionId}/analyze-and-suggest`, {
        university_id: selectedUniversity.id,
        selected_document_ids: selectedDocuments.length > 0 ? selectedDocuments : null
      }, { timeout: 180000 });
      
      clearInterval(progressInterval);
      setAnalysisProgress(100);
      setProgressMessage('Analysis complete!');
      setProgressSubMessage('');
      
      const result = response.data;
      setAnalysisResult(result);
      setSuggestedPrograms(result.recommended_programs || []);
      setRecommendedDegreeType(result.recommended_degree_type || '');
      
      // Auto-select first recommended program
      if (result.recommended_programs?.length > 0 && result.recommended_programs[0].id) {
        setSelectedProgram(result.recommended_programs[0]);
      }
      
      // Small delay to show 100% complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setCurrentPhase('suggestions');
      
      // Build profile summary
      const profile = result.student_profile || {};
      const personal = profile.personal || {};
      const education = profile.education || {};
      
      let profileSummary = "";
      if (personal.full_name) profileSummary += `**Name:** ${personal.full_name}\n`;
      if (personal.nationality) profileSummary += `**Nationality:** ${personal.nationality}\n`;
      if (personal.email) profileSummary += `**Email:** ${personal.email}\n`;
      if (education.highest_degree) profileSummary += `**Education:** ${education.highest_degree}`;
      if (education.field_of_study) profileSummary += ` in ${education.field_of_study}`;
      if (education.institution) profileSummary += ` from ${education.institution}`;
      profileSummary += "\n";
      if (education.gpa) profileSummary += `**GPA:** ${education.gpa}\n`;
      
      addAIMessage(
        `**Analysis Complete!** 📊\n\n**Student Profile:**\n${profileSummary}\n**Recommended Degree Type:** ${result.recommended_degree_type}\n*${result.degree_type_reason || ''}*\n\n**Assessment:** ${result.assessment_summary || 'Ready to proceed with application.'}\n\nI've analyzed the documents and recommend the following programs. Please select one or let me proceed with my top recommendation.`,
        'program_suggestions'
      );
      
    } catch (error) {
      console.error('Analysis error:', error);
      
      // Reset progress state
      setAnalysisProgress(0);
      setProgressStage('upload');
      setProgressMessage('');
      setProgressSubMessage('');
      
      // Provide more specific error messages
      let errorMessage = "Unknown error occurred";
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = "The analysis is taking longer than expected. This may happen with large documents. Please try again.";
      } else if (error.response?.status === 402) {
        errorMessage = "Insufficient credits. Please top up your credits to continue.";
      } else if (error.response?.status === 500) {
        errorMessage = error.response?.data?.detail || "Server error during analysis. Please try again.";
      } else if (error.message === 'Network Error') {
        errorMessage = "Network connection issue. Please check your internet and try again.";
      } else {
        errorMessage = error.response?.data?.detail || error.message;
      }
      
      addAIMessage(`I encountered an issue during analysis: ${errorMessage}`, 'document_upload');
      setCurrentPhase('documents');
      toast.error(errorMessage);
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  // Handle program selection from suggestions
  const handleProgramSelection = (prog) => {
    setSelectedProgram(prog);
    
    // Handle "Select program later" option
    if (prog.id === 'select_later') {
      addUserMessage("I'll select the program later");
      setIsThinking(true);
      
      setTimeout(() => {
        setIsThinking(false);
        setCurrentPhase('create');
        addAIMessage(
          `No problem! The application will be created without a specific program.\n\n**Ready to Create Application**\n\nI will now create the application with:\n- All uploaded documents attached\n- Student profile pre-filled from document analysis\n- Program: To be selected later\n\nYou can select the program manually after the application is created.\n\nClick **"Create Application"** to proceed.`,
          'create_application'
        );
      }, 500);
      return;
    }
    
    addUserMessage(`I'd like to apply for ${prog.name}`);
    setIsThinking(true);
    
    setTimeout(() => {
      setIsThinking(false);
      setCurrentPhase('create');
      addAIMessage(
        `Excellent choice! **${prog.name}** is selected.\n\n**Ready to Create Application**\n\nI will now create the application with:\n- All uploaded documents attached\n- Student profile pre-filled from document analysis\n- Selected program: ${prog.name}\n\nClick **"Create Application"** to proceed.`,
        'create_application'
      );
    }, 500);
  };
  
  // Create application with documents
  const createApplication = async () => {
    // Validate degree type is selected
    if (!selectedDegreeType) {
      toast.error('Please select a degree type before creating the application');
      return;
    }
    
    addUserMessage("Yes, please create the application with all documents attached");
    setIsCreating(true);
    setIsThinking(true);
    
    try {
      // Use longer timeout for application creation (3 minutes)
      // Don't send program_id if "select_later" was chosen
      const programId = selectedProgram?.id === 'select_later' ? null : (selectedProgram?.id || null);
      
      const response = await api().post(`/ai-profiler/sessions/${sessionId}/create-application`, {
        university_id: selectedUniversity.id,
        program_id: programId,
        degree_type: selectedDegreeType,
        nationality_id: selectedNationality  // Include selected nationality
      }, { timeout: 180000 });
      
      setCreatedAppId(response.data.application_id);
      setCreatedAppType(response.data.type || null);
      setCurrentPhase('complete');
      
      setIsThinking(false);
      
      const docsAttached = response.data.documents_attached || documents.length;
      const isBpoApp = response.data.type === 'bpo_application';
      
      if (isBpoApp) {
        // BPO Application created - show different message
        const ccn = response.data.ccn || 'N/A';
        const programDisplay = selectedProgram?.id === 'select_later' ? 'To be selected later' : (selectedProgram?.name || 'Not specified');
        addAIMessage(
          `🎉 **BPO Application Created Successfully!**\n\n**Details:**\n- **CCN:** ${ccn}\n- **University:** ${selectedUniversity.name}\n- **Program:** ${programDisplay}\n- **Documents Attached:** ${docsAttached} document(s)\n\nThe application has been created in your BPO Agent Portal with all extracted information pre-filled.\n\nYou can view and complete the application in your Agent Portal.`,
          'application_created'
        );
        toast.success('BPO Application created successfully!');
      } else {
        // Regular application
        const programDisplay = selectedProgram?.id === 'select_later' ? 'To be selected later' : (selectedProgram?.name || 'Not specified');
        addAIMessage(
          `🎉 **Application Created Successfully!**\n\n**Details:**\n- **University:** ${selectedUniversity.name}\n- **Program:** ${programDisplay}\n- **Recommended Degree Type:** ${recommendedDegreeType}\n- **Documents Attached:** ${docsAttached} document(s)\n\nAll documents have been attached to their relevant document types. The student profile has been pre-filled from the analyzed documents.\n\nYou can view the application in the Applications section.`,
          'application_created'
        );
        toast.success('Application created with all documents attached!');
      }
      
      triggerCreditUpdate();
    } catch (error) {
      setIsThinking(false);
      console.error('Create application error:', error);
      
      // Provide more specific error messages
      let errorMessage = "Unknown error occurred";
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = "The operation is taking longer than expected. Please try again.";
      } else if (error.response?.status === 402) {
        errorMessage = "Insufficient credits. Please top up your credits to continue.";
      } else if (error.message === 'Network Error') {
        errorMessage = "Network connection issue. Please check your internet and try again.";
      } else {
        errorMessage = error.response?.data?.detail || error.message || "Unknown error";
      }
      
      addAIMessage(`I encountered an issue creating the application: ${errorMessage}. Please try again.`);
      toast.error(`Failed to create application: ${errorMessage}`);
    } finally {
      setIsCreating(false);
    }
  };
  
  // Handle chat input submission
  const handleChatSubmit = async () => {
    if (!chatInput.trim() || !sessionId) return;
    
    const userMsg = chatInput.trim();
    addUserMessage(userMsg);
    setChatInput('');
    setIsThinking(true);
    
    try {
      const response = await api().post('/ai-profiler/chat', {
        session_id: sessionId,
        message: userMsg
      });
      
      const aiResponse = response.data.response || response.data.message || "I'm here to help with your application.";
      
      setTimeout(() => {
        setIsThinking(false);
        addAIMessage(aiResponse);
      }, 800);
    } catch (error) {
      setIsThinking(false);
      addAIMessage("I'm sorry, I couldn't process that. Please try again.");
    }
  };
  
  // Render document list
  // Toggle document selection for analysis
  const toggleDocumentSelection = (docId) => {
    setSelectedDocuments(prev => {
      if (prev.includes(docId)) {
        return prev.filter(id => id !== docId);
      } else {
        return [...prev, docId];
      }
    });
  };
  
  // Select all documents
  const selectAllDocuments = () => {
    setSelectedDocuments(documents.map(d => d.id));
  };
  
  // Deselect all documents
  const deselectAllDocuments = () => {
    setSelectedDocuments([]);
  };
  
  // Proceed to document selection phase
  const proceedToDocumentSelection = () => {
    // Auto-select all documents by default
    setSelectedDocuments(documents.map(d => d.id));
    setCurrentPhase('select_docs');
    addAIMessage(
      `I found **${documents.length} documents**. Please select which documents you want me to analyze for program recommendations.\n\nYou can check/uncheck documents below, then click "Analyze Selected Documents" to proceed.`,
      'document_selection'
    );
  };
  
  const renderDocumentList = () => {
    if (documents.length === 0) return null;
    
    // Show checkboxes in both 'documents' and 'select_docs' phases
    const showCheckboxes = currentPhase === 'documents' || currentPhase === 'select_docs';
    
    return (
      <div className="mt-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground font-medium">
            {showCheckboxes ? `Select Documents for Analysis (${selectedDocuments.length}/${documents.length} selected)` : `Uploaded Documents (${documents.length})`}
          </p>
          {showCheckboxes && documents.length > 0 && (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllDocuments}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={deselectAllDocuments}>
                Clear
              </Button>
            </div>
          )}
        </div>
        <div className="space-y-1 max-h-60 overflow-y-auto">
          {documents.map(doc => {
            const isSelected = selectedDocuments.includes(doc.id);
            return (
              <div 
                key={doc.id} 
                className={`flex items-center gap-2 p-2 rounded-lg group transition-all cursor-pointer ${
                  showCheckboxes 
                    ? isSelected 
                      ? 'bg-primary/10 border border-primary/30' 
                      : 'bg-muted/30 border border-transparent hover:bg-muted/50'
                    : 'bg-muted/50'
                }`}
                onClick={() => showCheckboxes && toggleDocumentSelection(doc.id)}
              >
                {showCheckboxes && (
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                    isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30 hover:border-primary/50'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                  </div>
                )}
                <FileText className={`w-4 h-4 flex-shrink-0 ${isSelected && showCheckboxes ? 'text-primary' : 'text-muted-foreground'}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">{doc.filename}</p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {doc.document_type_name}
                    {doc.original_filename && doc.original_filename !== doc.filename && (
                      <span className="ml-1 opacity-60">(was: {doc.original_filename})</span>
                    )}
                  </p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }}
                  className="opacity-0 group-hover:opacity-100 p-1 text-destructive hover:text-destructive/80 transition-opacity"
                  title="Remove document"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  
  // Render interactive component in chat
  const renderChatComponent = (componentType) => {
    switch (componentType) {
      case 'university_select':
        return (
          <div className="mt-3 p-4 bg-muted/30 rounded-xl border space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <GraduationCap className="w-5 h-5" />
              <span className="text-sm font-medium">Select University</span>
            </div>
            <Select onValueChange={handleUniversitySelect} disabled={currentPhase !== 'university'}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose a university..." />
              </SelectTrigger>
              <SelectContent>
                <UniversityOptions universities={universities} />
              </SelectContent>
            </Select>
          </div>
        );
      
      case 'nationality_select':
        return (
          <div className="mt-3 p-4 bg-muted/30 rounded-xl border space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <Globe className="w-5 h-5" />
              <span className="text-sm font-medium">Select Nationality</span>
            </div>
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search country..."
                value={nationalitySearchQuery}
                onChange={(e) => setNationalitySearchQuery(e.target.value)}
                className="pl-9"
                disabled={currentPhase !== 'nationality'}
              />
            </div>
            {/* Country List */}
            <ScrollArea className="h-[200px] border rounded-md">
              <div className="p-2 space-y-1">
                {countries
                  .filter(c => c.name?.toLowerCase().includes(nationalitySearchQuery.toLowerCase()))
                  .map((country) => (
                    <div
                      key={country.id}
                      onClick={() => currentPhase === 'nationality' && handleNationalitySelect(country.id)}
                      className={`p-2 rounded-md cursor-pointer transition-colors flex items-center gap-2 ${
                        selectedNationality === country.id
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-primary/10'
                      } ${currentPhase !== 'nationality' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Globe className="w-4 h-4" />
                      <span className="text-sm">{country.name}</span>
                    </div>
                  ))}
                {countries.filter(c => c.name?.toLowerCase().includes(nationalitySearchQuery.toLowerCase())).length === 0 && (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    No countries match "{nationalitySearchQuery}"
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        );
        
      case 'document_upload':
        return (
          <div className="mt-3 p-4 bg-muted/30 rounded-xl border space-y-4">
            <div className="flex items-center gap-2 text-primary">
              <Upload className="w-5 h-5" />
              <span className="text-sm font-medium">Document Upload</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Individual file upload */}
              <div className="space-y-2">
                <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                  <SelectTrigger className="w-full h-9 text-xs">
                    <SelectValue placeholder="Document type..." />
                  </SelectTrigger>
                  <SelectContent>
                    <DocTypeOptions docTypes={documentTypes} />
                  </SelectContent>
                </Select>
                <Button 
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!selectedDocType || isUploading || currentPhase !== 'documents'}
                  className="w-full h-9 text-xs"
                >
                  {isUploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Paperclip className="w-3 h-3 mr-1" />}
                  Upload File
                </Button>
                <input type="file" ref={fileInputRef} onChange={(e) => handleFileUpload(e.target.files?.[0], selectedDocType)} className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
              </div>
              
              {/* ZIP upload */}
              <div 
                className={`p-3 rounded-lg border-2 border-dashed hover:border-primary/50 hover:bg-primary/5 transition-all cursor-pointer flex flex-col items-center justify-center ${currentPhase !== 'documents' ? 'opacity-50 pointer-events-none' : ''}`}
                onClick={() => zipInputRef.current?.click()}
              >
                <FolderArchive className="w-6 h-6 text-primary mb-1" />
                <span className="text-xs text-muted-foreground">Upload ZIP</span>
                <input type="file" ref={zipInputRef} onChange={(e) => handleZipUpload(e.target.files?.[0])} className="hidden" accept=".zip" />
              </div>
            </div>
            
            {renderDocumentList()}
            
            {documents.length > 0 && currentPhase === 'documents' && (
              <div className="space-y-2 p-3 bg-primary/5 rounded-lg border-2 border-primary/20 animate-in slide-in-from-bottom-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">{documents.length} document(s) ready</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Coins className="w-4 h-4" />
                    <Badge variant="outline">~3 Credits</Badge>
                  </div>
                </div>
                
                <Button 
                  onClick={runAnalysisAndSuggest}
                  disabled={isAnalyzing || selectedDocuments.length === 0}
                  className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
                  size="lg"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Analyzing... ({analysisProgress}%)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Analyze {selectedDocuments.length} Selected Document{selectedDocuments.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
                
                {selectedDocuments.length === 0 && (
                  <p className="text-xs text-amber-600 text-center">Please select at least one document to analyze</p>
                )}
              </div>
            )}
            
            {currentPhase === 'select_docs' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Coins className="w-4 h-4" />
                    <span>AI Analysis Cost:</span>
                  </div>
                  <Badge variant="outline">~3 Credits</Badge>
                </div>
                
                <Button 
                  onClick={runAnalysisAndSuggest}
                  disabled={isAnalyzing || selectedDocuments.length === 0}
                  className="w-full"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Analyzing... ({analysisProgress}%)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Analyze {selectedDocuments.length} Selected Document{selectedDocuments.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
                
                {selectedDocuments.length === 0 && (
                  <p className="text-xs text-amber-600 text-center">Please select at least one document to analyze</p>
                )}
                
                <Button 
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPhase('documents')}
                  className="w-full text-xs"
                >
                  ← Back to Upload More Documents
                </Button>
              </div>
            )}
          </div>
        );
        
      case 'document_selection':
        return (
          <div className="mt-3 p-4 bg-muted/30 rounded-xl border space-y-4">
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Select Documents for Analysis</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Check the documents you want me to analyze. I'll extract information from selected documents to recommend suitable programs.
            </p>
            {renderDocumentList()}
            
            {selectedDocuments.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Coins className="w-4 h-4" />
                    <span>AI Analysis Cost:</span>
                  </div>
                  <Badge variant="outline">~3 Credits</Badge>
                </div>
                
                <Button 
                  onClick={runAnalysisAndSuggest}
                  disabled={isAnalyzing}
                  className="w-full"
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Analyzing... ({analysisProgress}%)
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Analyze {selectedDocuments.length} Selected Document{selectedDocuments.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        );
        
      case 'program_suggestions':
        return (
          <div className="mt-3 p-4 bg-muted/30 rounded-xl border space-y-4">
            <div className="flex items-center gap-2 text-primary">
              <Star className="w-5 h-5" />
              <span className="text-sm font-medium">Recommended Programs</span>
              {recommendedDegreeType && (
                <Badge variant="secondary" className="ml-2">{recommendedDegreeType}</Badge>
              )}
            </div>
            
            <div className="space-y-2">
              {suggestedPrograms.map((prog, idx) => (
                <Card 
                  key={idx} 
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedProgram?.name === prog.name ? 'ring-2 ring-primary' : ''
                  }`}
                  onClick={() => handleProgramSelection(prog)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          {idx === 0 && <Badge className="bg-amber-500 text-white">Top Pick</Badge>}
                          <h4 className="font-medium text-sm">{prog.name}</h4>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{prog.reason}</p>
                      </div>
                      <Button 
                        size="sm" 
                        variant={selectedProgram?.name === prog.name ? "default" : "outline"}
                        onClick={(e) => { e.stopPropagation(); handleProgramSelection(prog); }}
                      >
                        {selectedProgram?.name === prog.name ? <Check className="w-4 h-4" /> : 'Select'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {/* Select Program Manually Option */}
              <Card 
                className={`cursor-pointer transition-all hover:shadow-md border-dashed ${
                  selectedProgram?.id === 'manual_select' || showManualProgramDropdown ? 'ring-2 ring-primary' : ''
                }`}
                onClick={(e) => { e.stopPropagation(); handleManualProgramClick(); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-muted-foreground" />
                        <h4 className="font-medium text-sm text-muted-foreground">Select Program Manually</h4>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Choose from all programs offered by this university</p>
                    </div>
                    <Button 
                      size="sm" 
                      variant={showManualProgramDropdown ? "default" : "outline"}
                      onClick={(e) => { e.stopPropagation(); handleManualProgramClick(); }}
                    >
                      {loadingAllPrograms ? <Loader2 className="w-4 h-4 animate-spin" /> : showManualProgramDropdown ? 'Selecting...' : 'Browse'}
                    </Button>
                  </div>
                  
                  {/* Manual Program Dropdown */}
                  {showManualProgramDropdown && (
                    <div className="mt-4 space-y-2">
                      {loadingAllPrograms ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          <span className="ml-2 text-sm text-muted-foreground">Loading programs...</span>
                        </div>
                      ) : allUniversityPrograms.length > 0 ? (
                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground font-medium">Select a program from {selectedUniversity?.name}:</p>
                          {/* Search Input */}
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="Search programs..."
                              value={programSearchQuery}
                              onChange={(e) => setProgramSearchQuery(e.target.value)}
                              className="pl-9"
                            />
                          </div>
                          {/* Scrollable Program List */}
                          <ScrollArea className="h-[200px] border rounded-md">
                            <div className="p-2 space-y-1">
                              {allUniversityPrograms
                                .filter(prog => 
                                  prog.name?.toLowerCase().includes(programSearchQuery.toLowerCase()) ||
                                  prog.degree_type_name?.toLowerCase().includes(programSearchQuery.toLowerCase())
                                )
                                .map((prog) => (
                                  <div
                                    key={prog.id}
                                    onClick={() => handleManualProgramSelect(prog.id)}
                                    className="p-2 rounded-md hover:bg-primary/10 cursor-pointer transition-colors border border-transparent hover:border-primary/20"
                                  >
                                    <div className="font-medium text-sm">{prog.name}</div>
                                    {prog.degree_type_name && (
                                      <div className="text-xs text-muted-foreground">{prog.degree_type_name}</div>
                                    )}
                                  </div>
                                ))}
                              {allUniversityPrograms.filter(prog => 
                                prog.name?.toLowerCase().includes(programSearchQuery.toLowerCase()) ||
                                prog.degree_type_name?.toLowerCase().includes(programSearchQuery.toLowerCase())
                              ).length === 0 && (
                                <div className="text-center py-4 text-sm text-muted-foreground">
                                  No programs match "{programSearchQuery}"
                                </div>
                              )}
                            </div>
                          </ScrollArea>
                          <p className="text-xs text-muted-foreground text-center">
                            {allUniversityPrograms.length} program(s) available
                          </p>
                        </div>
                      ) : (
                        <div className="text-center py-3 text-sm text-muted-foreground">
                          <AlertCircle className="w-4 h-4 inline mr-1" />
                          No programs found for this university
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {suggestedPrograms.length === 0 && (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No specific programs found. You can still proceed to create the application.</p>
                  <Button 
                    className="mt-2" 
                    onClick={() => {
                      setCurrentPhase('create');
                      addAIMessage("Ready to create the application. Click 'Create Application' to proceed.", 'create_application');
                    }}
                  >
                    Continue Without Program
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
        
      case 'create_application':
        return (
          <div className="mt-3 p-4 bg-muted/30 rounded-xl border space-y-4">
            <div className="flex items-center gap-2 text-primary">
              <ClipboardCheck className="w-5 h-5" />
              <span className="text-sm font-medium">Create Application</span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">University:</span>
                <span className="font-medium">{selectedUniversity?.name}</span>
              </div>
              {selectedProgram && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Program:</span>
                  <span className="font-medium">{selectedProgram.name}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Documents:</span>
                <span className="font-medium">{documents.length} file(s)</span>
              </div>
            </div>
            
            {/* Degree Type Selection - Required */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-1">
                Degree Type <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedDegreeType}
                onChange={(e) => setSelectedDegreeType(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select degree type...</option>
                {degreeTypes.map(dt => (
                  <option key={dt.id || dt.name} value={dt.name}>
                    {dt.name}
                  </option>
                ))}
              </select>
              {recommendedDegreeType && (
                <p className="text-xs text-muted-foreground">
                  AI Recommended: <span className="font-medium">{recommendedDegreeType}</span>
                </p>
              )}
            </div>
            
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Coins className="w-4 h-4" />
                <span>Application Creation:</span>
              </div>
              <Badge>1 Credit</Badge>
            </div>
            
            <Button 
              onClick={createApplication}
              disabled={isCreating || currentPhase !== 'create' || !selectedDegreeType}
              className="w-full"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Creating Application...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Create Application with Documents
                </>
              )}
            </Button>
            {!selectedDegreeType && (
              <p className="text-xs text-amber-600 text-center">
                Please select a degree type to continue
              </p>
            )}
          </div>
        );
        
      case 'application_created':
        const viewUrl = createdAppType === 'bpo_application' 
          ? `/bpo-agent-applications?app=${createdAppId}`
          : `/applications/${createdAppId}`;
        const buttonText = createdAppType === 'bpo_application'
          ? 'Go to Agent Portal'
          : 'View Application';
        
        return (
          <div className="mt-3 p-4 bg-green-500/10 rounded-xl border border-green-500/30 space-y-4">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                {createdAppType === 'bpo_application' ? 'BPO Application Created!' : 'Application Created Successfully!'}
              </span>
            </div>
            
            <div className="flex gap-3">
              <Button 
                onClick={() => window.location.href = viewUrl}
                className="flex-1"
              >
                <Eye className="w-4 h-4 mr-2" />
                {buttonText}
              </Button>
              <Button 
                variant="outline"
                onClick={startNewApplication}
                disabled={isLoading}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Button>
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  // Render message
  const renderMessage = (msg, idx) => {
    if (msg.type === 'ai') {
      return (
        <div key={idx} className="flex items-start gap-3 animate-fade-in">
          <AIAvatar />
          <div className="flex-1 space-y-1">
            <div className="p-4 rounded-xl bg-muted/50 border-l-2 border-primary">
              <p className="text-sm whitespace-pre-line">{msg.content.split('**').map((part, i) => 
                i % 2 === 1 ? <strong key={i}>{part}</strong> : part
              )}</p>
              {msg.component && renderChatComponent(msg.component)}
            </div>
            <span className="text-[10px] text-muted-foreground px-2">{msg.timestamp}</span>
          </div>
        </div>
      );
    } else {
      return (
        <div key={idx} className="flex items-start gap-3 justify-end animate-fade-in">
          <div className="flex-1 max-w-[80%] space-y-1">
            <div className="p-4 rounded-xl bg-primary text-primary-foreground ml-auto">
              <p className="text-sm">{msg.content}</p>
            </div>
            <span className="text-[10px] text-muted-foreground px-2 text-right block">{msg.timestamp}</span>
          </div>
          <UserAvatar />
        </div>
      );
    }
  };
  
  // No credits view
  if (!hasCredits) {
    return (
      <div className="h-full flex items-center justify-center p-8">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-amber-500" />
              Insufficient Credits
            </CardTitle>
            <CardDescription>
              You need at least 1 credit to use AI Counselor. Please top up your credits to continue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => window.location.href = '/settings?tab=credits'} className="w-full">
              Add Credits
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-card">
        <div className="flex items-center gap-3">
          <AIAvatar size="lg" />
          <div>
            <h1 className="font-semibold text-lg">AI Counselor</h1>
            <p className="text-xs text-muted-foreground">Create applications with AI-powered document analysis</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            <Coins className="w-3 h-3" />
            {creditBalance} credits
          </Badge>
          <Button variant="outline" size="sm" onClick={startNewApplication} disabled={isLoading || currentPhase === 'init'}>
            <Plus className="w-4 h-4 mr-1" />
            New
          </Button>
        </div>
      </div>
      
      {/* Progress indicator */}
      <div className="px-4 py-2 border-b bg-muted/30">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className={`flex items-center gap-1 ${currentPhase === 'university' ? 'text-primary font-medium' : currentPhase !== 'init' ? 'text-green-600' : ''}`}>
            <Building2 className="w-3 h-3" />
            <span>1. University</span>
            {currentPhase !== 'init' && currentPhase !== 'university' && <Check className="w-3 h-3" />}
          </div>
          <ArrowRight className="w-3 h-3" />
          <div className={`flex items-center gap-1 ${['documents', 'select_docs'].includes(currentPhase) ? 'text-primary font-medium' : ['analysis', 'suggestions', 'create', 'complete'].includes(currentPhase) ? 'text-green-600' : ''}`}>
            <Upload className="w-3 h-3" />
            <span>2. Upload & Select</span>
            {['analysis', 'suggestions', 'create', 'complete'].includes(currentPhase) && <Check className="w-3 h-3" />}
          </div>
          <ArrowRight className="w-3 h-3" />
          <div className={`flex items-center gap-1 ${['analysis', 'suggestions'].includes(currentPhase) ? 'text-primary font-medium' : ['create', 'complete'].includes(currentPhase) ? 'text-green-600' : ''}`}>
            <Sparkles className="w-3 h-3" />
            <span>3. AI Analysis</span>
            {['create', 'complete'].includes(currentPhase) && <Check className="w-3 h-3" />}
          </div>
          <ArrowRight className="w-3 h-3" />
          <div className={`flex items-center gap-1 ${currentPhase === 'create' ? 'text-primary font-medium' : currentPhase === 'complete' ? 'text-green-600' : ''}`}>
            <UserPlus className="w-3 h-3" />
            <span>4. Create</span>
            {currentPhase === 'complete' && <Check className="w-3 h-3" />}
          </div>
        </div>
      </div>
      
      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => renderMessage(msg, idx))}
        
        {/* Show progress loader during upload or analysis */}
        {(isUploading || isAnalyzing) && (
          <ProgressLoader 
            progress={isAnalyzing ? analysisProgress : uploadProgress}
            stage={progressStage}
            message={progressMessage}
            subMessage={progressSubMessage}
          />
        )}
        
        {/* Show thinking indicator for other operations */}
        {isThinking && !isUploading && !isAnalyzing && <ThinkingIndicator />}
        <div ref={chatEndRef} />
      </div>
      
      {/* Chat input */}
      <div className="p-4 border-t bg-card">
        <div className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleChatSubmit()}
            placeholder="Ask me anything about the application process..."
            className="flex-1 px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            disabled={isThinking || isUploading || isAnalyzing || currentPhase === 'init'}
          />
          <Button 
            onClick={handleChatSubmit}
            disabled={!chatInput.trim() || isThinking || currentPhase === 'init'}
            size="icon"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          You can ask questions anytime during the process. I'm here to help!
        </p>
      </div>
    </div>
  );
};

export default AIProfilerChat;
